import * as yup from 'yup';
import { ValidationError } from 'yup';

import { useTranslations } from 'hooks/useTranslations';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { IMask } from 'react-imask';
import { CHARS_LIMIT } from 'components/organisms/FormCustomField';
import { ERegExpTypes } from 'constants/RegExpTypes';
import { EDictionaryPrefixTypesUniqueId } from 'helpers/dictionary';
import { isStringEmpty } from 'utils/string';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export const useIndLEFormSchema = (
  customFields: ICustomFieldByObjectType[],
  requiredFieldsName: string[],
  hiddenFieldsName: string[],
  isWeaponLicence: boolean,
  initialCustomFields?: ILegalEntityCustomField[],
) => {
  const { t } = useTranslations();

  const customFieldsValidations: { [key: string]: any } = {};

  customFields.map((customField) => {
    const baseValidation = yup.string().nullable();

    if (customField.customFieldType === ECustomFieldType.TEXT && customField.mandatory) {
      customFieldsValidations[customField.name] = baseValidation
        .required(t('general.field.validation.required'))
        .max(
          CHARS_LIMIT,
          t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
        );
    } else if (customField.customFieldType === ECustomFieldType.TEXT) {
      customFieldsValidations[customField.name] = baseValidation.max(
        CHARS_LIMIT,
        t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
      );
    } else if (customField.mandatory) {
      customFieldsValidations[customField.name] = baseValidation.required(
        t('general.field.validation.required'),
      );
    }

    return false;
  });

  let schema = yup.object({
    firstName: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
    lastName: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
    secondSurname: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          hiddenFieldsName.includes(EFieldsOptional.secondSurname) ||
          !requiredFieldsName.includes(EFieldsOptional.secondSurname)
        ) {
          return true;
        }
        return !isStringEmpty(value);
      })
      .max(150, t('general.field.validation.maxCharacters').replace('{0}', '150'))
      .trim(),
    middleNames: yup
      .string()
      .nullable()
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
    dateOfBirth: yup
      .string()
      .nullable()
      .required(t('general.field.validation.required'))
      .test(
        'age-validation',
        t('legalEntity.validation.maxDate').replace('{0}', '255'),
        (value) => {
          if (typeof value !== 'string') {
            return false;
          }
          const maxValidDateOfBirth = new Date();
          const dateOfBirth = new Date(value);
          return dateOfBirth.valueOf() <= maxValidDateOfBirth.valueOf();
        },
      ),
    nationalityUniqueId: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (!isWeaponLicence) {
          return true;
        }
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        ) {
          return true;
        }

        return !isStringEmpty(value);
      })
      .trim(),
    placeOfBirth: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          !requiredFieldsName.includes(EFieldsOptional.placeOfBirth) ||
          hiddenFieldsName.includes(EFieldsOptional.placeOfBirth)
        ) {
          return true;
        }
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        )
          return true;

        return !isStringEmpty(value);
      })
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
      .trim(),
    height: yup
      .string()
      .nullable()
      .matches(
        new RegExp(ERegExpTypes.height),
        t('general.field.validation.valueIsNotCorrect'),
      )
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          hiddenFieldsName.includes(EFieldsOptional.height) ||
          !requiredFieldsName.includes(EFieldsOptional.height)
        ) {
          return true;
        }
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        )
          return true;

        return !isStringEmpty(value);
      })
      .max(10, t('general.field.validation.valueIsNotCorrect')),
    occupation: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          hiddenFieldsName.includes(EFieldsOptional.occupation) ||
          !requiredFieldsName.includes(EFieldsOptional.occupation)
        ) {
          return true;
        }
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        )
          return true;

        return !isStringEmpty(value);
      })
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64')),
    genderUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    visibilityUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    prefixGroupUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    prefixValueUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    identityDocumentTypeUniqueId: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        )
          return true;

        if (isStringEmpty(context.parent.identityDocumentNumber)) return true;

        return !isStringEmpty(value);
      }),
    identityDocumentNumber: yup
      .string()
      .nullable()
      .max(20, t('general.field.validation.maxCharacters').replace('{0}', '20'))
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          context.parent.prefixGroupUniqueId !== EDictionaryPrefixTypesUniqueId.civilianPrefix
        )
          return true;

        if (isStringEmpty(context.parent.identityDocumentTypeUniqueId)) return true;

        return !isStringEmpty(value);
      }),
    primaryWeaponLicencingAuthorityUniqueId: yup
      .string()
      .nullable()
      .test(
        'primaryWeaponLicencingAuthorityUniqueId-validation',
        t('general.field.validation.required'),
        (value, obj: any) => {
          if (
            !value &&
            obj.parent.prefixGroupUniqueId === EDictionaryPrefixTypesUniqueId.civilianPrefix &&
            isWeaponLicence
          ) {
            return false;
          }

          return true;
        },
      ),
    secondaryWeaponLicencingAuthorityUniqueId: yup
      .string()
      .nullable()
      .test(
        'secondaryWeaponLicencingAuthorityUniqueId-validation',
        t('general.field.validation.unique'),
        (value, obj: any) => {
          if (!value) {
            return true;
          }

          if (obj.parent.primaryWeaponLicencingAuthorityUniqueId === value) {
            return false;
          }

          return true;
        },
      ),
    ...customFieldsValidations,
  });

  customFields.map((customField) => {
    if (customField.inputMaskEnabled && customField.inputMask) {
      schema = schema.test(`customFieldMaskValidation${customField.uniqueId}`, '', (obj) => {
        const mask = customField?.inputMask;
        if (mask) {
          const masked = IMask.createMask({ mask } as IMask.AnyMaskedOptions);
          // @ts-expect-error: required by update
          if (obj?.[customField.name]) {
            if (initialCustomFields) {
              type Obj = typeof obj;
              const key = customField.name as keyof Obj;
              const val = obj?.[key];

              const oldVal = initialCustomFields.find(
                (oldCustomField: ILegalEntityCustomField) =>
                  oldCustomField.customFieldName === customField.name,
              )?.value;

              if (oldVal && val && oldVal === val) {
                return true;
              }
            }

            // @ts-expect-error: required by update
            masked.resolve(obj?.[customField.name] as string);
            if (!masked.isComplete) {
              return new yup.ValidationError(
                `Wrong input mask value (${mask})`,
                null,
                customField.name,
              );
            }
          }
        }

        return true;
      });
    }
    return false;
  });

  return { schema };
};

export const useOrgLEFormSchema = (
  customFields: ICustomFieldByObjectType[],
  hiddenFieldsName: EFieldsOptional[],
  requiredFieldsName: EFieldsOptional[],
  isWeaponLicence: boolean,
  initialCustomFields?: ILegalEntityCustomField[],
) => {
  const { t } = useTranslations();

  const customFieldsValidations: { [key: string]: any } = {};

  customFields.map((customField) => {
    const baseValidation = yup.string().nullable();

    if (customField.customFieldType === ECustomFieldType.TEXT && customField.mandatory) {
      customFieldsValidations[customField.name] = baseValidation
        .required(t('general.field.validation.required'))
        .max(
          CHARS_LIMIT,
          t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
        );
    } else if (customField.customFieldType === ECustomFieldType.TEXT) {
      customFieldsValidations[customField.name] = baseValidation.max(
        CHARS_LIMIT,
        t('general.field.validation.maxCharacters').replace('{0}', CHARS_LIMIT.toString()),
      );
    } else if (customField.mandatory) {
      customFieldsValidations[customField.name] = baseValidation.required(
        t('general.field.validation.required'),
      );
    }

    return false;
  });

  let schema = yup.object({
    organizationName: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(80, t('general.field.validation.maxCharacters').replace('{0}', '80'))
      .trim(),
    organisationTypeUniqueIds: yup
      .array()
      .of(yup.string())
      .test(
        'required',
        t('general.field.validation.required'),
        (value) => !(!value || value.length === 0),
      ),
    organisationCategoryUniqueId: yup
      .string()
      .required(t('general.field.validation.required'))
      .trim(),
    departmentName: yup
      .string()
      .nullable()
      .test('required', t('general.field.validation.required'), (value, context) => {
        if (
          hiddenFieldsName.includes(EFieldsOptional.departmentName) ||
          !requiredFieldsName.includes(EFieldsOptional.departmentName)
        ) {
          return true;
        }
        return !isStringEmpty(value);
      })
      .max(80, t('general.field.validation.maxCharacters').replace('{0}', '80'))
      .trim(),
    description: yup
      .string()
      .nullable()
      .max(250, t('general.field.validation.maxCharacters').replace('{0}', '250'))
      .trim(),
    visibilityUniqueId: yup.string().required(t('general.field.validation.required')).trim(),
    primaryWeaponLicencingAuthorityUniqueId: yup
      .string()
      .nullable()
      .test(
        'primaryWeaponLicencingAuthorityUniqueId-validation',
        t('general.field.validation.required'),
        (value, obj: any) =>
          !(
            !value &&
            obj.parent.organisationCategoryUniqueId === EOrganisationCategory.civilian &&
            isWeaponLicence
          ),
      ),
    secondaryWeaponLicencingAuthorityUniqueId: yup
      .string()
      .nullable()
      .test(
        'secondaryWeaponLicencingAuthorityUniqueId-validation',
        t('general.field.validation.unique'),
        (value, obj: any) => {
          if (!value) return true;

          return obj.parent.primaryWeaponLicencingAuthorityUniqueId !== value;
        },
      ),
    ...customFieldsValidations,
  });

  customFields.map((customField) => {
    if (customField.inputMaskEnabled && customField.inputMask) {
      schema = schema.test(`customFieldMaskValidation${customField.uniqueId}`, '', (obj) => {
        const mask = customField?.inputMask;
        if (mask) {
          const masked = IMask.createMask({ mask } as IMask.AnyMaskedOptions);
          // @ts-expect-error: required by update
          if (obj?.[customField.name]) {
            if (initialCustomFields) {
              type Obj = typeof obj;
              const key = customField.name as keyof Obj;
              const val = obj?.[key];

              const oldVal = initialCustomFields.find(
                (oldCustomField: ILegalEntityCustomField) =>
                  oldCustomField.customFieldName === customField.name,
              )?.value;

              if (oldVal && val && oldVal === val) {
                return true;
              }
            }

            // @ts-expect-error: required by update
            masked.resolve(obj?.[customField.name] as string);
            if (!masked.isComplete) {
              return new yup.ValidationError(
                `Wrong input mask value (${mask})`,
                null,
                customField.name,
              );
            }
          }
        }

        return true;
      });
    }
    return false;
  });

  return { schema };
};

export const useLEContactSchema = (type: ELegalEntityTypes) => {
  const { t } = useTranslations();

  const errorsOfFields = (fields: string[]) => {
    let errors;
    if (fields.length === 1) {
      errors = [new ValidationError(t('general.field.validation.required'), null, fields[0])];
    } else {
      errors = fields
        .map(
          (field) =>
            new ValidationError(t('general.field.validation.anyIsRequired'), null, field),
        )
        .filter(Boolean);
    }

    return new ValidationError(errors);
  };

  const emailOrPhoneNumberRequired = (obj: any): ValidationError | boolean => {
    const isEmailEmpty = !obj.email || !obj.email.trim();
    const isPhonePrefixEmpty =
      !obj.codeDictionaryUniqueId || !obj.codeDictionaryUniqueId.trim();
    const isPhoneNumberEmpty = !obj.value || !obj.value.trim();

    if (isEmailEmpty && isPhonePrefixEmpty && isPhoneNumberEmpty) {
      return errorsOfFields(['email', 'codeDictionaryUniqueId', 'value']);
    }

    if (isEmailEmpty && isPhoneNumberEmpty) {
      return errorsOfFields(['email', 'value']);
    }

    if (isEmailEmpty && isPhonePrefixEmpty) {
      return errorsOfFields(['email', 'codeDictionaryUniqueId']);
    }

    if (isPhonePrefixEmpty && !isPhoneNumberEmpty) {
      return errorsOfFields(['codeDictionaryUniqueId']);
    }

    if (!isPhonePrefixEmpty && isPhoneNumberEmpty) {
      return errorsOfFields(['value']);
    }

    return true;
  };

  if (type === ELegalEntityTypes.individual) {
    const leContactSchema = yup
      .object({
        email: yup
          .string()
          .email(t('general.field.email.validation.incorrect'))
          .max(254, t('general.field.validation.maxCharacters').replace('{0}', '254'))
          .trim(),
        codeDictionaryUniqueId: yup.string().trim(),
        value: yup
          .string()
          .nullable()
          .test('isMatching', t('general.field.validation.valueIsNotCorrect'), (value) => {
            if (value) {
              const regex = new RegExp(ERegExpTypes.onlyDigits);
              return regex.test(value);
            }

            return true;
          })
          .max(15, t('general.field.validation.maxNumberCharacters').replace('{0}', '15'))
          .min(5, t('general.field.validation.minNumberCharacters').replace('{0}', '5'))
          .transform((value, originalValue) =>
            !originalValue || originalValue.trim() === '' ? null : originalValue,
          )
          .trim(),
      })
      .test('emailOrPhoneNumberRequired', (obj) => emailOrPhoneNumberRequired(obj));
    return { leContactSchema };
  }
  const leContactSchema = yup
    .object({
      name: yup
        .string()
        .required(t('general.field.validation.required'))
        .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
        .trim(),
      role: yup
        .string()
        .required(t('general.field.validation.required'))
        .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
        .trim(),
      email: yup
        .string()
        .email(t('general.field.email.validation.incorrect'))
        .max(254, t('general.field.validation.maxCharacters').replace('{0}', '254'))
        .trim(),
      codeDictionaryUniqueId: yup.string().trim(),
      value: yup
        .string()
        .nullable()
        .test('isMatching', t('general.field.validation.valueIsNotCorrect'), (value) => {
          if (value) {
            const regex = new RegExp(ERegExpTypes.onlyDigits);
            return regex.test(value);
          }

          return true;
        })
        .max(15, t('general.field.validation.maxNumberCharacters').replace('{0}', '15'))
        .min(5, t('general.field.validation.minNumberCharacters').replace('{0}', '5'))
        .transform((value, originalValue) =>
          !originalValue || originalValue.trim() === '' ? null : originalValue,
        )
        .trim(),
    })
    .test('emailOrPhoneNumberRequired', (obj) => emailOrPhoneNumberRequired(obj));
  return { leContactSchema };
};

export const useAddressSchema = () => {
  const { t } = useTranslations();
  const addressSchema = yup.object({
    typeUniqueId: yup.string().required(t('general.field.validation.required')).nullable(),
    isDefault: yup.boolean(),
    line1: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
      .trim(),
    line2: yup
      .string()
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
      .trim(),
    postalCode: yup
      .string()
      .notRequired()
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
      .trim(),
    city: yup
      .string()
      .required(t('general.field.validation.required'))
      .nullable()
      .max(64, t('general.field.validation.maxCharacters').replace('{0}', '64'))
      .trim(),
    regionUniqueId: yup.string().notRequired().nullable(),
    countryUniqueId: yup.string().required(t('general.field.validation.required')).nullable(),
    latitude: yup.string().required(t('addAddress.validation.locationRequired')),
    longitude: yup.string().required(t('addAddress.validation.locationRequired')),
  });
  return { addressSchema };
};

export const useLEGroupSchema = () => {
  const { t } = useTranslations();
  const leGroupSchema = yup.object({
    name: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(128, t('general.field.validation.maxCharacters').replace('{0}', '128'))
      .trim(),
  });
  return { leGroupSchema };
};

export const useLEPreviousNamesSurnamesSchema = () => {
  const { t } = useTranslations();
  const schema = yup.object({
    firstName: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
    middleNames: yup
      .string()
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
    lastName: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(100, t('general.field.validation.maxCharacters').replace('{0}', '100'))
      .trim(),
  });
  return { schema };
};
